import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export function PageSizeSelect({
  value,
  options,
  onChange,
  disabled = false,
  'aria-label': ariaLabel = 'Rows per page',
  'data-testid': testId,
}: {
  value: number;
  options: number[];
  onChange: (size: number) => void;
  disabled?: boolean;
  'aria-label'?: string;
  'data-testid'?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            'inline-flex h-8 min-w-[4.25rem] items-center justify-between gap-1.5 rounded-lg border border-[#E4E7EC] bg-white px-2.5 text-[12.5px] font-medium text-[#101828] outline-none transition',
            'hover:bg-slate-50 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/15',
            'disabled:cursor-not-allowed disabled:opacity-40',
          )}
        >
          <span>{value}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[#98A2B3]" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="z-50 w-[5.5rem] p-0 rounded-xl bg-white border border-[#E4E7EC] shadow-[0_8px_20px_rgba(16,24,40,0.08)]"
        role="listbox"
        aria-label={ariaLabel}
      >
        <div className="max-h-56 overflow-y-auto p-1.5">
          {options.map((size) => {
            const selected = size === value;
            return (
              <button
                key={size}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(size);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left text-[12.5px] font-medium transition',
                  selected
                    ? 'bg-brand-soft text-brand-cta'
                    : 'text-[#101828] hover:bg-[#F9FAFB]',
                )}
              >
                <span>{size}</span>
                {selected ? (
                  <Check className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
